import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { db } from '../../firebase/firebase-config';
import Swal from 'sweetalert2';

export const PasoAsignarServicios = ({ siguiente, anterior }) => {
    const { hashtag } = useSelector(state => state.auth);
    const [colaboradoras, setColaboradoras] = useState([]);
    const [catalogo, setCatalogo] = useState([]);
    const [clasificaciones, setClasificaciones] = useState([]);
    const [seleccion, setSeleccion] = useState({}); // { '@Nombre': Set de nombres de servicios }
    const [colActualIdx, setColActualIdx] = useState(0);
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        const cargar = async () => {
            try {
                // Cargar colaboradoras
                const colSnap = await db.collection(`general/${hashtag}/consultores`).get();
                const cols = colSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setColaboradoras(cols);

                // Inicializar selección con servicios ya asignados
                const selInicial = {};
                for (const col of cols) {
                    const listaSnap = await db.doc(`general/${hashtag}/consultores/${col.id}/servicios/lista`).get();
                    if (listaSnap.exists && listaSnap.data().servicios) {
                        selInicial[col.id] = new Set(listaSnap.data().servicios.map(s => s.servicio));
                    } else {
                        selInicial[col.id] = new Set();
                    }
                }
                setSeleccion(selInicial);

                // Cargar catálogo
                const catSnap = await db.doc(`general/${hashtag}/servicios/catalogo`).get();
                const servicios = catSnap.exists ? (catSnap.data().servicios || []) : [];
                setCatalogo(servicios);

                // Cargar clasificaciones
                const empresaSnap = await db.doc(`general/${hashtag}`).get();
                const clStr = empresaSnap.data()?.clasificaciones || '';
                if (clStr) {
                    const grupos = clStr.split(';').map(parte => {
                        const [nombre, rango] = parte.split('/');
                        const [min, max] = rango.split('-').map(Number);
                        return { nombre, min, max };
                    });
                    setClasificaciones(grupos);
                }
            } catch (e) {
                console.log('Error cargando datos:', e);
            } finally {
                setCargando(false);
            }
        };
        cargar();
    }, [hashtag]);

    const toggleServicio = (colId, nombreServicio) => {
        const nueva = new Set(seleccion[colId]);
        if (nueva.has(nombreServicio)) {
            nueva.delete(nombreServicio);
        } else {
            nueva.add(nombreServicio);
        }
        setSeleccion({ ...seleccion, [colId]: nueva });
    };

    const toggleGrupo = (colId, serviciosGrupo) => {
        const sel = seleccion[colId];
        const todosSeleccionados = serviciosGrupo.every(s => sel.has(s.servicio));
        const nueva = new Set(sel);
        serviciosGrupo.forEach(s => {
            if (todosSeleccionados) nueva.delete(s.servicio);
            else nueva.add(s.servicio);
        });
        setSeleccion({ ...seleccion, [colId]: nueva });
    };

    const getServiciosPorGrupo = () => {
        if (clasificaciones.length === 0) {
            return [{ nombre: null, servicios: catalogo }];
        }
        return clasificaciones.map(cl => ({
            nombre: cl.nombre,
            servicios: catalogo.filter(s => s.orden >= cl.min && s.orden <= cl.max),
        })).filter(g => g.servicios.length > 0);
    };

    const handleGuardar = async () => {
        setGuardando(true);
        try {
            const col = colaboradoras[colActualIdx];
            const serviciosSeleccionados = catalogo
                .filter(s => seleccion[col.id]?.has(s.servicio))
                .map((s, i) => ({ ...s, orden: s.orden || i + 1 }));

            await db.doc(`general/${hashtag}/consultores/${col.id}/servicios/lista`).set({
                servicios: serviciosSeleccionados
            });

            // Actualizar noServicios
            await db.doc(`general/${hashtag}/consultores/${col.id}`).update({
                noServicios: serviciosSeleccionados.length
            });

            if (colActualIdx < colaboradoras.length - 1) {
                setColActualIdx(colActualIdx + 1);
            } else {
                siguiente();
            }
        } catch (e) {
            Swal.fire('Error', 'No se pudieron guardar los servicios. Intenta de nuevo.', 'error');
        } finally {
            setGuardando(false);
        }
    };

    if (cargando) {
        return (
            <div className="app-container" style={{ alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: 32 }}>💅</div>
                <p style={{ color: 'var(--pink-800)', marginTop: 12 }}>Cargando...</p>
            </div>
        );
    }

    if (colaboradoras.length === 0 || catalogo.length === 0) {
        return (
            <div className="app-container">
                <div className="app-header">
                    <h1>Asignar servicios</h1>
                    <p>Casi terminamos 🎉</p>
                </div>
                <div className="screen-body">
                    <div className="info-box">
                        <div className="info-box-body">
                            {catalogo.length === 0
                                ? 'No tienes servicios en el catálogo. Vuelve al paso anterior y agrega al menos uno.'
                                : 'No tienes colaboradoras registradas.'}
                        </div>
                    </div>
                </div>
                <div className="screen-footer">
                    <button className="btn-secondary" onClick={anterior}>Volver</button>
                </div>
            </div>
        );
    }

    const col = colaboradoras[colActualIdx];
    const grupos = getServiciosPorGrupo();
    const totalSeleccionados = seleccion[col.id]?.size || 0;

    return (
        <div className="app-container">
            <div className="app-header">
                <h1>Servicios de {col.id.replace('@', '')}</h1>
                <p>Colaboradora {colActualIdx + 1} de {colaboradoras.length}</p>
            </div>

            {/* Mini progress de colaboradoras */}
            <div style={{ display: 'flex', gap: 4, padding: '8px 20px', background: 'var(--pink-50)', borderBottom: '0.5px solid var(--border)' }}>
                {colaboradoras.map((c, i) => (
                    <div
                        key={c.id}
                        onClick={() => setColActualIdx(i)}
                        style={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            background: i < colActualIdx ? 'var(--pink-600)' : i === colActualIdx ? 'var(--pink-400)' : 'var(--border)',
                            cursor: 'pointer',
                        }}
                        title={c.id}
                    />
                ))}
            </div>

            <div className="screen-body">
                <div className="info-box" style={{ marginTop: 0, marginBottom: 12 }}>
                    <div className="info-box-body">
                        Selecciona los servicios que atiende <strong>{col.id.replace('@', '')}</strong>.
                        {totalSeleccionados > 0 && <span style={{ color: 'var(--pink-600)' }}> ({totalSeleccionados} seleccionados)</span>}
                    </div>
                </div>

                {grupos.map((grupo, gi) => {
                    const todosSeleccionados = grupo.servicios.every(s => seleccion[col.id]?.has(s.servicio));
                    const algunoSeleccionado = grupo.servicios.some(s => seleccion[col.id]?.has(s.servicio));

                    return (
                        <div key={gi} style={{ marginBottom: 12 }}>
                            {grupo.nombre && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '8px 12px',
                                        background: 'var(--pink-100)',
                                        borderRadius: 8,
                                        marginBottom: 4,
                                        cursor: 'pointer',
                                        border: '0.5px solid var(--border)',
                                    }}
                                    onClick={() => toggleGrupo(col.id, grupo.servicios)}
                                >
                                    <div style={{
                                        width: 20, height: 20, borderRadius: 4,
                                        background: todosSeleccionados ? 'var(--pink-600)' : algunoSeleccionado ? 'var(--pink-200)' : '#fff',
                                        border: `1.5px solid ${todosSeleccionados ? 'var(--pink-600)' : 'var(--border)'}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        {todosSeleccionados && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>}
                                        {algunoSeleccionado && !todosSeleccionados && <span style={{ color: 'var(--pink-600)', fontSize: 14, fontWeight: 700 }}>−</span>}
                                    </div>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--pink-900)' }}>{grupo.nombre}</span>
                                    <span style={{ fontSize: 12, color: 'var(--pink-800)', marginLeft: 'auto' }}>
                                        {grupo.servicios.filter(s => seleccion[col.id]?.has(s.servicio)).length}/{grupo.servicios.length}
                                    </span>
                                </div>
                            )}

                            {grupo.servicios.map((svc, si) => {
                                const seleccionado = seleccion[col.id]?.has(svc.servicio);
                                return (
                                    <div
                                        key={si}
                                        onClick={() => toggleServicio(col.id, svc.servicio)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            padding: '10px 12px',
                                            borderRadius: 8,
                                            marginBottom: 4,
                                            cursor: 'pointer',
                                            background: seleccionado ? 'var(--pink-50)' : '#fff',
                                            border: `0.5px solid ${seleccionado ? 'var(--pink-400)' : 'var(--border)'}`,
                                            marginLeft: grupo.nombre ? 8 : 0,
                                        }}
                                    >
                                        <div style={{
                                            width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                                            background: seleccionado ? 'var(--pink-600)' : '#fff',
                                            border: `1.5px solid ${seleccionado ? 'var(--pink-600)' : 'var(--border)'}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            {seleccionado && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--pink-900)' }}>{svc.servicio}</div>
                                            <div style={{ fontSize: 11, color: 'var(--pink-800)', marginTop: 1 }}>
                                                ${svc.precio}{svc.precioMaximo ? `–$${svc.precioMaximo}` : ''} · {svc.duracion} min
                                                {svc.montoAnticipo > 0 ? ` · Anticipo $${svc.montoAnticipo}` : ''}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>

            <div className="screen-footer">
                <button className="btn-primary" onClick={handleGuardar} disabled={guardando}>
                    {guardando ? 'Guardando...' : colActualIdx < colaboradoras.length - 1
                        ? `Siguiente: ${colaboradoras[colActualIdx + 1].id.replace('@', '')} →`
                        : 'Terminar ✓'}
                </button>
                {colActualIdx > 0 && (
                    <button className="btn-secondary" onClick={() => setColActualIdx(colActualIdx - 1)}>
                        ← {colaboradoras[colActualIdx - 1].id.replace('@', '')}
                    </button>
                )}
            </div>
        </div>
    );
};
